/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Cell, ExternalStyles, FormControl, Grid, HFlow, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { FormSectionAccordion } from 'components/accordion/FormSectionAccordion'
import { DecimalField, ErrorField, NumberField, RadioField } from 'components/form'
import { TipoGlicemia } from 'graphql/types.generated'
import { useField } from 'react-final-form'
import { tipoGlicemiaRecord } from 'types/enums'
import { MetaPath } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'
import { meta as soapMeta } from 'view/atendimentos/atendimento-individual/model'
import { getSoapFieldLabelGroup } from 'view/atendimentos/detail/soap/fields-summary-modal/getSoapFieldLabelGroup'

export interface SinaisVitaisAccordionModel {
  openedAccordions?: (string | number)[]
  pressaoArterialSistolica?: string
  pressaoArterialDiastolica?: string
  frequenciaRespiratoria?: string
  frequenciaCardiaca?: string
  temperatura?: string
  saturacaoO2?: string
  glicemia?: string
  tipoGlicemia?: TipoGlicemia
}

interface SinaisVitaisAccordionProps {
  name: MetaPath<SinaisVitaisAccordionModel>
  style?: ExternalStyles
}

export function SinaisVitaisAccordion(props: Readonly<SinaisVitaisAccordionProps>) {
  const {
    name,
    style = css`
      margin: 0;
    `,
  } = props

  const {
    input: { value },
  } = useField<SinaisVitaisAccordionModel>(name.absolutePath(), {
    subscription: { value: true },
  })

  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    label,
    fields: { frequenciaCardiaca, frequenciaRespiratoria, glicemia, saturacaoO2, temperatura, tipoGlicemia },
  } = getSoapFieldLabelGroup(soapMeta.objetivo.sinaisVitais)

  return (
    <FormSectionAccordion name={name.openedAccordions} title={label} uuid='sinaisVitais' style={style}>
      <div style={{ padding: '1rem' }}>
        <Grid>
          <Cell xs={12} lg={4}>
            <FormControl label='Pressão arterial (mmHg)'>
              <HFlow>
                <NumberField name={name.pressaoArterialSistolica} label='' maxLength={3} />
                <Text component='p' fontSize={1.25}>
                  /
                </Text>
                <NumberField name={name.pressaoArterialDiastolica} label='' maxLength={3} />
              </HFlow>
            </FormControl>
          </Cell>

          <Cell xs={4} lg={3}>
            <NumberField name={name.frequenciaRespiratoria} label={frequenciaRespiratoria.label} maxLength={3} />
          </Cell>

          <Cell xs={3} lg={1} />

          <Cell xs={4} lg={3}>
            <NumberField name={name.frequenciaCardiaca} label={frequenciaCardiaca.label} maxLength={3} />
          </Cell>

          <Cell xs={4} lg={3}>
            <DecimalField name={name.temperatura} label={temperatura.label} maxLength={4} precisao={1} />
          </Cell>

          <Cell xs={3} lg={1} />

          <Cell xs={4} lg={3}>
            <NumberField name={name.saturacaoO2} label={saturacaoO2.label} maxLength={3} />
          </Cell>
        </Grid>

        <Grid style={styles.border}>
          <Cell size={4}>
            <NumberField name={name.glicemia} label={glicemia.label} maxLength={3} />
          </Cell>

          <Cell xs={12} lg={8}>
            <FormControl label={tipoGlicemia.label} required={!isEmpty(value.glicemia)}>
              <VFlow vSpacing={0.5}>
                <HFlow style={{ marginLeft: '-0.25rem' }}>
                  <RadioField
                    name={name.tipoGlicemia}
                    label={tipoGlicemiaRecord[TipoGlicemia.NAO_ESPECIFICADO]}
                    value={TipoGlicemia.NAO_ESPECIFICADO}
                    disabled={!value.glicemia}
                  />
                  <RadioField
                    name={name.tipoGlicemia}
                    label={tipoGlicemiaRecord[TipoGlicemia.JEJUM]}
                    value={TipoGlicemia.JEJUM}
                    disabled={!value.glicemia}
                  />
                  <RadioField
                    name={name.tipoGlicemia}
                    label={tipoGlicemiaRecord[TipoGlicemia.PREPRANDIAL]}
                    value={TipoGlicemia.PREPRANDIAL}
                    disabled={!value.glicemia}
                  />
                  <RadioField
                    name={name.tipoGlicemia}
                    label={tipoGlicemiaRecord[TipoGlicemia.POSPRANDIAL]}
                    value={TipoGlicemia.POSPRANDIAL}
                    disabled={!value.glicemia}
                  />
                </HFlow>
                <ErrorField name={name.tipoGlicemia} />
              </VFlow>
            </FormControl>
          </Cell>
        </Grid>
      </div>
    </FormSectionAccordion>
  )
}

const createStyles = (theme: Theme) => ({
  border: css`
    border-top: 0.063rem solid ${theme.pallete.gray.c80};
    margin-top: 1rem;
    padding-top: 0.5rem;
  `,
})
